// TanStack Query keys and hooks for tasks and reply tracking. `task` and `reply` SSE events
// refresh these lists (see feedEvents.ts); every mutation also invalidates the task lists it may
// have touched (cheap: a few rows).
import { queryOptions, useQuery, type QueryClient } from "@tanstack/react-query";
import type { TaskListView, TaskView, ThreadStatusView } from "@shared/api";
import type { ThreadStatus } from "@shared/core";
import { tasksApi } from "./tasksApi";

export const taskKeys = {
  all: ["tasks"] as const,
  list: (view: TaskListView) => ["tasks", view] as const,
  task: (id: string) => ["task", id] as const,
  replies: (status: ThreadStatus | "all") => ["replies", status] as const,
};

export function taskListQuery(view: TaskListView) {
  return queryOptions({
    queryKey: taskKeys.list(view),
    queryFn: ({ signal }) => tasksApi.list(view, signal),
    staleTime: 15_000,
    // Task events keep this fresh; a slow poll covers the time the stream is down.
    refetchInterval: 120_000,
  });
}

export function useTaskList(view: TaskListView, enabled = true) {
  return useQuery({ ...taskListQuery(view), enabled });
}

export function taskQuery(id: string) {
  return queryOptions({
    queryKey: taskKeys.task(id),
    queryFn: ({ signal }) => tasksApi.get(id, signal),
    staleTime: 30_000,
  });
}

export function useTask(id: string | null | undefined) {
  return useQuery({ ...taskQuery(id ?? ""), enabled: !!id });
}

/** Store a task the server returned and refresh every list it might now belong to. */
export function acceptTask(queryClient: QueryClient, task: TaskView): void {
  queryClient.setQueryData(taskKeys.task(task.id), task);
  invalidateTasks(queryClient);
}

export function invalidateTasks(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all });
}

/** Remove a task from every cached list (optimistic done/drop/snooze); returns a rollback. */
export function removeTaskLocally(queryClient: QueryClient, taskId: string): () => void {
  const before = queryClient.getQueriesData<TaskView[]>({ queryKey: taskKeys.all });
  queryClient.setQueriesData<TaskView[]>({ queryKey: taskKeys.all }, (list) =>
    list?.some((t) => t.id === taskId) ? list.filter((t) => t.id !== taskId) : list,
  );
  return () => {
    for (const [key, data] of before) queryClient.setQueryData(key, data);
  };
}

// ---------------------------------------------------------------------------------------------
// Reply tracking

export function repliesQuery(status: ThreadStatus | "all") {
  return queryOptions({
    queryKey: taskKeys.replies(status),
    // "all" feeds the card badges: one request, mapped by thread key.
    queryFn: ({ signal }) =>
      tasksApi.replies(
        status === "all" ? undefined : status,
        status === "all" ? 1000 : 500,
        signal,
      ),
    staleTime: 60_000,
    refetchInterval: 300_000,
  });
}

export function useReplies(status: ThreadStatus | "all", enabled = true) {
  return useQuery({ ...repliesQuery(status), enabled });
}

const statusMaps = new WeakMap<readonly ThreadStatusView[], Map<string, ThreadStatusView>>();

/** Thread key → status, computed once per fetched list (shared by every card row). */
export function statusMap(list: readonly ThreadStatusView[]): Map<string, ThreadStatusView> {
  let map = statusMaps.get(list);
  if (!map) {
    map = new Map(list.map((s) => [s.threadKey, s] as const));
    statusMaps.set(list, map);
  }
  return map;
}

/** The owner's side of a card's thread, when it is worth a badge (to reply / awaiting reply). */
export function useThreadReplyStatus(threadKey: string | null): ThreadStatusView | null {
  const all = useReplies("all");
  if (!threadKey || !all.data) return null;
  const hit = statusMap(all.data).get(threadKey) ?? null;
  return hit && (hit.status === "to_reply" || hit.status === "awaiting_reply") ? hit : null;
}

export function invalidateReplies(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["replies"] });
}
